import React from "react";

import cx from "classnames";

import { Flex } from "../Flex";
import { Pill } from "../Pill";
import { ProfilePic } from "../ProfilePic";
import { Text } from "../Text";

import SearchWithTypeAhead, { defaultI18n } from "./SearchWithTypeAhead";
import styles from "./UserSearchWithTypeAhead.scss";

/**
 * @param {UserData} data
 * @param {string} query
 * @returns boolean
 */
function userDataMatchesQuery(data, query) {
  const lQuery = query.toLowerCase();
  if (data.name.toLowerCase().includes(lQuery)) {
    return true;
  }
  if (data.helperText.toLowerCase().includes(lQuery)) {
    return true;
  }
  if (data.tag.toLowerCase().includes(lQuery)) {
    return true;
  }
  return false;
}

function highlight(original, query) {
  const parts = original.split(new RegExp(`(${query})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <b key={i}>{part}</b>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

/**
 * @typedef {object} UserData
 * @prop {string} id
 * @prop {string} avatar
 * @prop {string} initials
 * @prop {string} name
 * @prop {string} [helperText] The text to render under the users' name. Tends to be the user's location, title, or department
 * @prop {string} [tag] The text in the tag to render to the right of the user's name and helper text
 */

/**
 * @typedef {object} UserResultProps
 * @param {UserData} data
 * @param {string} query The search query
 * @param {number} index The index of this item in the list
 * @param {number} selectedIndex The index of the item selected in the list
 * @param {Function} setSelectedIndex For hovering
 * @param {Function} onSubmit
 */

/**
 * An uncontrolled user search box with type-ahead completion.
 * @param {UserResultProps} props
 */
export function UserResult({
  data,
  query,
  index,
  selectedIndex,
  setSelectedIndex,
  onSubmit,
}) {
  return (
    <button
      className={cx(styles.userRow, {
        [styles.userRowSelected]: index === selectedIndex,
      })}
      onMouseOver={() => setSelectedIndex(index)}
      onFocus={() => setSelectedIndex(index)}
      onClick={() => onSubmit(data)}
    >
      <div className={styles.profilePic}>
        <div className={styles.profilePicContainer}>
          <ProfilePic
            dimensions={38}
            user={{
              id: data.id,
              initials: data.initials,
              name: data.name,
              avatar: data.avatar,
            }}
          />
        </div>
      </div>
      <Flex direction="column" className={styles.name}>
        <Text size="small" className={styles.trim}>
          {highlight(data.name, query)}
        </Text>
        {data.helperText && (
          <Text
            size="small"
            className={styles["trim--helperText"]}
            data-testid="helperText"
          >
            {highlight(data.helperText, query)}
          </Text>
        )}
      </Flex>
      {data.tag && (
        <div className={styles.tag} data-testid="tag">
          <Pill text={data.tag} className={styles.trim} />
        </div>
      )}
    </button>
  );
}

/**
 * @typedef {object} InputProps
 * @property {string} [helperText=""] - Helper text for the input
 * @property {string} [error=""] - Error message for the input
 * @property {string} [type="text"] - Type of the input
 * @property {number} [maxLength] - Maximum length of the input
 * @property {string} [placeholder="Search"] - Placeholder text for the input
 * @property {boolean} [required=false] - Whether the input is required
 */

/**
 * @typedef {object} SearchI18n
 * @property {string} [search="Search for"]
 * @property {string} [clear="Clear"]
 */

/**
 * @typedef {object} UserSearchWithTypeAheadProps
 * @property {string} id - The ID of the input box
 * @property {string} [defaultValue=""] - The default value of the search box
 * @property {Function} onChange - When the value of the search changes, we invoke this function. Signature: `(value: string) => void`
 * @property {UserData[]} [userData=[]] - User data, if it exists. Use all of the data you can find, and the component will automatically show matching results
 * @property {SearchI18n} [i18n] - Internationalization strings
 * @property {boolean} [fullWidth=false] - Whether the component should take up the full width
 * @property {Function} [onClear] - If set, we add a "clear" button to the left of the search icon. When that button is activated, we call this function
 * @property {Function} onSubmit - When a user wants to search, we invoke this function. Signature: `(query: string) => void`
 * @property {(data: UserData, query: string) => boolean} [matchFunction] - Function used to filter the data that will be displayed in the results
 * @property {InputProps} [inputProps={}] - Props to attach to the input box
 */

/**
 * @param {UserSearchWithTypeAheadProps} props - Props for the component
 * ⚠️ If you are trying to implement a people search component, you likely don't need to use this
 * packages/people-search should have most of what you need out of the box where this is very bare bones.
 */
export default function UserSearchWithTypeAhead({
  defaultValue = "",
  userData = [],
  onChange,
  onClear,
  fullWidth = false,
  onSubmit,
  inputProps,
  i18n = {},
  matchFunction = userDataMatchesQuery,
}) {
  const i18nValues = Object.assign({}, defaultI18n, i18n);
  const [value, setValue] = React.useState(defaultValue);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const [menuOpen, setMenuOpen] = React.useState(value.length >= 2);
  const [typeAheadResults, visibleItems] = React.useMemo(() => {
    // Show nothing if the value length is below our minimum size
    if (value.length < 2) {
      return [[], []];
    }
    const visibleData = userData.filter((data) => matchFunction(data, value));
    const handleSearch = (data) => {
      setMenuOpen(false);
      onSubmit(data);
    };
    const resultsElements = [
      value.length > 0 ? (
        <button
          className={styles.searchFor}
          key="default"
          onClick={() => handleSearch(value)}
        >
          {i18nValues.search}: <b className={styles.searchForQuery}>{value}</b>
        </button>
      ) : null,
      ...visibleData.map((data, i) => (
        <UserResult
          data={data}
          key={data.id}
          query={value}
          selectedIndex={selectedIndex}
          index={i}
          setSelectedIndex={setSelectedIndex}
          onClick={() => handleSearch(data)}
          onSubmit={handleSearch}
        />
      )),
    ];
    return [resultsElements, visibleData];
  }, [value, userData, selectedIndex]);

  React.useEffect(() => {
    if (!menuOpen) {
      setMenuOpen(true);
    }
  }, [value]);

  return (
    <SearchWithTypeAhead
      value={value}
      onChange={(newValue) => {
        setValue(newValue);
        onChange(newValue);
      }}
      onClear={() => {
        setValue("");
        onClear ? onClear() : null;
      }}
      onSubmit={(value) => {
        onSubmit(value);
        setMenuOpen(false);
      }}
      typeAheadResults={typeAheadResults}
      menuOpen={menuOpen}
      onSetOpen={setMenuOpen}
      fullWidth={fullWidth}
      i18n={i18n}
      inputProps={{
        ...inputProps,
        onKeyDown: (e) => {
          if (e.key === "Escape") {
            setMenuOpen(false);
          }
          if (e.key === "Enter") {
            e.preventDefault();
            if (
              visibleItems.length === 0 ||
              selectedIndex < 0 ||
              selectedIndex >= visibleItems.length
            ) {
              onSubmit(value);
            } else {
              onSubmit(visibleItems[selectedIndex]);
            }
            e.target.blur();
            setMenuOpen(false);
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedIndex(
              selectedIndex > 0
                ? selectedIndex - 1
                : typeAheadResults.length - 1
            );
            setMenuOpen(true);
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedIndex(
              selectedIndex < typeAheadResults.length ? selectedIndex + 1 : 0
            );
            setMenuOpen(true);
          }
        },
      }}
    />
  );
}
